<template>
  <div
    id="app"
    :class="[
      $router.currentRoute.name + '-page',
      {'ipad-safari' : iPadSafari},
      {'is-full-page': $route.meta && $route.meta.fullPage},
      viewportOrientation + '-mode'
    ]"
  >
    <div
      class="section-category"
      v-if="$router.currentRoute.name !== 'login'"
      v-show="$router.currentRoute.name == 'order'"
    >
      <grid/>
      <category-filter/>
    </div>
    <router-view />
    <MonthReport ref="monthlyDeliveryHost" style="position:absolute;left:-10000px;top:0;width:1024px;pointer-events:none" />
    <order-detail
      v-if="$router.currentRoute.name !== 'login'"
      :payment-review-mode="$router.currentRoute.name === 'pay'"
    />
    <mobile-portrait-nav v-if="$router.currentRoute.name !== 'login'"/>
    <message-modal v-if="message"/>
    <confirm-modal v-if="confirm"/>
    <input-pad-holder />
    <spinner />
    <portal-target name="bodyModalOption"></portal-target>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import MessageModal from '@/components/MessageModal.vue';
import ConfirmModal from '@/components/ConfirmModal.vue';
import OrderDetail from '@/components/OrderDetail.vue';
import InputPadHolder from '@/components/InputPadHolder.vue';
import Spinner from '@/components/Spinner.vue';
import MobilePortraitNav from '@/components/MobilePortraitNav.vue';
import SettingService from '@/services/SettingService';
import AuthService from '@/services/AuthService';
import {
  SET_ACTIVE_ELEMENT,
  ON_INPUT_PAD_BLUR,
  PURGE_AUTH
} from '@/store/mutation-types';
import CategoryFilter from '@/components/CategoryFilter.vue';
import Grid from '@/components/GridLayout.vue';
import MonthReport from '@/components/MonthReport.vue';
import MonthlyDeliveryCoordinator from '@/services/MonthlyDeliveryCoordinator';
import { Portal, PortalTarget, MountingPortal } from 'portal-vue'


//Vue.use(PortalVue)
export default {
  name: 'App',
  components: {
    MessageModal,
    ConfirmModal,
    OrderDetail,
    InputPadHolder,
    Spinner,
    MobilePortraitNav,
    CategoryFilter,
    Grid,
    MonthReport,
    Portal,
    PortalTarget,
    MountingPortal,
  },
  data() {
    return {
      viewportOrientation: 'landscape',
      orientationQuery: null,
      scrollHandler: null,
      viewportOrientationHandler: null,
    };
  },
  computed: {
    token() { return this.getMap('token', true) },
    user() { return this.getMap('user', true) },
    ...mapState({
      message: state => state.toggle.messageModal,
      confirm: state => state.toggle.confirmModal,
      inputHolderFocusing: state => state.inputPad.inputHolderFocusing,
    }),
    iPadSafari() {
      const ua = navigator.userAgent;
      const iOS = !!ua.match(/iPad/i) || !!ua.match(/iPhone/i);
      const webkit = !!ua.match(/WebKit/i);
      return iOS && webkit && !ua.match(/CriOS/i);
    },
    iPadChrome() {
      const ua = navigator.userAgent;
      const iOS = !!ua.match(/iPad/i) || !!ua.match(/iPhone/i);
      const webkit = !!ua.match(/WebKit/i);
      return iOS && webkit && ua.match(/CriOS/i);
    }
  },
  created() {
    let language = localStorage.getItem('pos-language');
    if(language) {
      this.$language.current = language;
    }
  },
  watch: {
    async token(newToken) {
      this.onTokenChanged();
    },
    inputHolderFocusing() {
      window.scrollTo(0, 0);
    },
  },
  async mounted() {
    this.monthlyDeliveryRunner = (context) => {
      const user = this.$store.state.auth.user || {};
      const location = user.location || {};
      const station = user.station || {};
      if (context && (String(context.locationId) !== String(location.id)
          || String(context.stationId) !== String(station.id))) return;
      return this.$refs.monthlyDeliveryHost.getMonthReport(null, null, true);
    };
    MonthlyDeliveryCoordinator.register(this.monthlyDeliveryRunner);
    document.addEventListener('keydown', this.disableKeyDown);
    if (this.$route.name === 'login' && this.token != '') {
      this.onTokenChanged();
    }
    document.addEventListener('click', this.onClick);
    this.syncViewportOrientation();
    this.bindViewportOrientationListener();
    const view = document.querySelector('#app > div:first-child');
    if(view) {
      this.scrollHandler = () => {
        this.$store.commit(ON_INPUT_PAD_BLUR, true);
      };
      view.addEventListener('scroll', this.scrollHandler);
    }

    if (!this.user.permissions ||
          this.user.permissions.length == 0) {
      this.$store.commit(PURGE_AUTH);
    }

    if(this.iPadChrome) {
      document.body.classList.add('ipad-chrome');
    }
  },
  beforeDestroy() {
    MonthlyDeliveryCoordinator.unregister(this.monthlyDeliveryRunner);
    const view = document.querySelector('#app > div:first-child');
    if(view && this.scrollHandler) {
      view.removeEventListener('scroll', this.scrollHandler);
    }
    this.unbindViewportOrientationListener();
    document.removeEventListener('click', this.onClick);
  },
  methods: {
    syncViewportOrientation() {
      const isPortrait = window.matchMedia
        ? window.matchMedia('(orientation: portrait)').matches
        : window.innerHeight >= window.innerWidth;
      const isPhoneWidth = window.matchMedia
        ? window.matchMedia('(max-width: 767px)').matches
        : window.innerWidth <= 767;
      const isPhonePortrait = isPortrait && isPhoneWidth;

      document.documentElement.style.setProperty('--cashier-viewport-height', `${window.innerHeight}px`);
      this.viewportOrientation = isPhonePortrait ? 'portrait' : 'landscape';
      document.body.classList.toggle('portrait-mode', isPhonePortrait);
      document.body.classList.toggle('landscape-mode', !isPhonePortrait);
    },
    bindViewportOrientationListener() {
      if (!window.matchMedia) return;

      this.viewportOrientationHandler = this.syncViewportOrientation.bind(this);
      this.orientationQuery = window.matchMedia('(orientation: portrait)');
      if (this.orientationQuery.addEventListener) {
        this.orientationQuery.addEventListener('change', this.viewportOrientationHandler);
      } else if (this.orientationQuery.addListener) {
        this.orientationQuery.addListener(this.viewportOrientationHandler);
      }

      window.addEventListener('resize', this.viewportOrientationHandler, { passive: true });
    },
    unbindViewportOrientationListener() {
      if (this.orientationQuery) {
        if (this.orientationQuery.removeEventListener) {
          this.orientationQuery.removeEventListener('change', this.viewportOrientationHandler);
        } else if (this.orientationQuery.removeListener) {
          this.orientationQuery.removeListener(this.viewportOrientationHandler);
        }
      }
      window.removeEventListener('resize', this.viewportOrientationHandler);
    },
    disableKeyDown(e) {
      if((e.srcElement.type === 'submit' || e.srcElement.type === 'button' || e.srcElement.tagName === 'BODY') && (e.keyCode === 13 || e.keyCode === 32)) {
        e.preventDefault();
        return false;
      }
    },
    onClick(evt) {
      this.$store.commit(SET_ACTIVE_ELEMENT, evt.target);
      if(![...evt.target.classList].includes('calculator-key')) {
        this.$store.dispatch('resetCurrentQuantityIncrease');
      }
    },
    async onTokenChanged() {
      if (this.token === '') {
        this.$router.replace({ name: 'login' });
        return;
      }
    }
  }
};
</script>
